import type { ZodType } from 'zod';

import { createProviderToken } from '@core/common-legacy';

/**
 * Agent-callable tool that executes on the client. Registered as a multi-provider
 * via `provideTellentFrontendTool` and forwarded to the agent in every
 * `RunAgentInput.tools`. The facade validates inbound args via `input`, awaits
 * `execute`, validates the return via `output`, and feeds a `ToolMessage` back
 * into the conversation so the agent can continue reasoning.
 */
export interface FrontendTool<TInput = unknown, TOutput = unknown> {
  /** Discriminator the LLM uses when calling. Must be unique per registration. */
  readonly name: string;
  /** Natural-language description surfaced to the LLM. */
  readonly description: string;
  /** JSON Schema for arguments — what the LLM sees in `RunAgentInput.tools[].parameters`. */
  readonly parameters: Record<string, unknown>;
  /** Zod schema validating arguments at runtime before `execute` runs. */
  readonly input: ZodType<TInput>;
  /** Zod schema validating the `execute` return before the tool result is sent back. */
  readonly output: ZodType<TOutput>;
  execute: (input: TInput) => Promise<TOutput>;
}

export const { provide: provideTellentFrontendTool, inject: injectTellentFrontendTools } =
  createProviderToken<FrontendTool>('TELLENT_FRONTEND_TOOLS')(true);
